import React, {
  useState,
  useEffect,
  useCallback,
  PropsWithChildren,
} from 'react';
import {
  MinimizedDrawersContext,
  MinimizedDrawerItem,
  MinimizedDrawersContextType,
} from './MinimizedDrawersContext';

const SESSION_STORAGE_KEY = 'minimizedDrawersState';

export const MinimizedDrawersProvider: React.FC<PropsWithChildren<{}>> = ({
  children,
}) => {
  const [minimizedDrawers, setMinimizedDrawers] = useState<
    MinimizedDrawerItem[]
  >([]);
  const [lastRestoredItem, setLastRestoredItem] =
    useState<MinimizedDrawerItem | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    try {
      const storedState = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (storedState) {
        const parsedState = JSON.parse(storedState);

        if (Array.isArray(parsedState)) {
          setMinimizedDrawers(parsedState);
        } else if (
          typeof parsedState === 'object' &&
          parsedState !== null &&
          Array.isArray(parsedState.minimizedDrawers)
        ) {
          setMinimizedDrawers(parsedState.minimizedDrawers);
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        'Failed to load minimized drawers from sessionStorage:',
        error,
      );
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    }
    setIsLoaded(true);
  }, []);

  useEffect(() => {
    if (isLoaded) {
      try {
        sessionStorage.setItem(
          SESSION_STORAGE_KEY,
          JSON.stringify(minimizedDrawers),
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(
          'Failed to save minimized drawers to sessionStorage:',
          error,
        );
      }
    }
  }, [minimizedDrawers, isLoaded]);

  const minimizeDrawer = useCallback(
    (id: string, title: string, data?: any) => {
      setMinimizedDrawers(prevDrawers => {
        if (prevDrawers.find(d => d.id === id)) {
          return prevDrawers.map(d =>
            d.id === id ? { ...d, title, data } : d,
          );
        }
        return [...prevDrawers, { id, title, data }];
      });
    },
    [],
  );

  const restoreDrawer = useCallback((id: string) => {
    let itemToRestore: MinimizedDrawerItem | undefined;
    setMinimizedDrawers(prevDrawers => {
      itemToRestore = prevDrawers.find(drawer => drawer.id === id);
      return prevDrawers.filter(drawer => drawer.id !== id);
    });
    if (itemToRestore) {
      setLastRestoredItem(itemToRestore);
    } else {
      setLastRestoredItem(null);
    }
  }, []);

  const clearLastRestoredItem = useCallback(() => {
    setLastRestoredItem(null);
  }, []);

  const clearMinimizedDrawer = useCallback(
    (id: string) => {
      setMinimizedDrawers(prevDrawers =>
        prevDrawers.filter(drawer => drawer.id !== id),
      );

      if (lastRestoredItem && lastRestoredItem.id === id) {
        setLastRestoredItem(null);
      }
    },
    [lastRestoredItem],
  );

  const isMinimized = useCallback(
    (id: string) => {
      return minimizedDrawers.some(drawer => drawer.id === id);
    },
    [minimizedDrawers],
  );

  const contextValue: MinimizedDrawersContextType = {
    minimizedDrawers,
    minimizeDrawer,
    restoreDrawer,
    isMinimized,
    lastRestoredItem,
    clearLastRestoredItem,
    clearMinimizedDrawer,
  };

  if (!isLoaded) {
    return null;
  }

  return (
    <MinimizedDrawersContext.Provider value={contextValue}>
      {children}
    </MinimizedDrawersContext.Provider>
  );
};
